import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { categoryAPI, blogAPI, userAPI } from '../utils/api';
import { Layout, Menu, List, Card, Typography, Spin, Button, message, Tag, Row, Col, Space, Modal, Popconfirm } from 'antd';
import { CalendarOutlined, EyeOutlined, LikeOutlined, FolderOutlined, RightOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import useDocumentTitle from '../hooks/useDocumentTitle';

const { Sider, Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const BlogPage = () => {
  const { username } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // 获取当前登录用户
  const currentUser = JSON.parse(localStorage.getItem('current_user') || 'null');
  const isOwner = currentUser?.username === username;

  // 根据是否选择分类动态设置网页标题
  const pageTitle = selectedCategory
    ? `${username} 的博客 - ${selectedCategory}`
    : `${username} 的博客`;
  useDocumentTitle(pageTitle, {
    prefix: '',
    suffix: ' - rtycn'
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const categoryId = queryParams.get('id');

    // 只处理博客列表功能
    if (categoryId) {
      // 指定分类的博客列表
      setSelectedCategory(categoryId);
      fetchBlogsByCategory(categoryId);
    } else {
      // 用户所有博客列表
      setSelectedCategory(null);
      fetchAllBlogs();
    }

    // 总是获取用户分类
    fetchCategories();
  }, [username, location.search]);

  // 获取用户分类
  const fetchCategories = async () => {
    try {
      // 验证用户是否存在
      const usersRes = await userAPI.getAllUsers();
      const userExists = usersRes.data?.some(u => u.username === username);
      if (!userExists) {
        navigate(`/${username}/error`, {
          state: {
            errorInfo: {
              title: '用户不存在',
              description: `用户 "${username}" 不存在`
            }
          },
          replace: true
        });
        return;
      }

      const response = await categoryAPI.getCategoriesByAuthor(username);
      setCategories(response.data || []);
    } catch {
      navigate(`/${username}/error`, {
        state: {
          errorInfo: {
            title: '数据加载失败',
            description: '无法获取分类列表，请稍后重试'
          }
        },
        replace: true
      });
    }
  };

  // 获取用户所有博客
  const fetchAllBlogs = async () => {
    try {
      setLoading(true);
      const response = await blogAPI.getBlogsByAuthor(username);
      // 按创建时间倒序排序
      const sortedBlogs = (response.data || []).sort((a, b) =>
        new Date(b.createTime) - new Date(a.createTime)
      );
      setBlogs(sortedBlogs);
    } catch {
      navigate(`/${username}/error`, {
        state: {
          errorInfo: {
            title: '数据加载失败',
            description: '无法获取博客列表，请稍后重试'
          }
        },
        replace: true
      });
    } finally {
      setLoading(false);
    }
  };

  // 获取指定分类的博客
  const fetchBlogsByCategory = async (categoryId) => {
    try {
      setLoading(true);
      const response = await blogAPI.getBlogsByCategory(categoryId);
      // 按创建时间倒序排序
      const sortedBlogs = (response.data || []).sort((a, b) =>
        new Date(b.createTime) - new Date(a.createTime)
      );
      setBlogs(sortedBlogs);
    } catch {
      message.error('获取分类博客失败');
    } finally {
      setLoading(false);
    }
  };


  // 分类菜单点击事件
  const handleCategoryClick = (categoryId) => {
    if (categoryId === 'all') {
      navigate(`/${username}/blog/`);
    } else {
      navigate(`/${username}/blog/?id=${categoryId}`);
    }
  };

  // 博客点击事件
  const handleBlogClick = (blog) => {
    navigate(`/${username}/blog/article/?id=${blog.id}`);
  };

  // 删除博客
  const handleDeleteBlog = async (blogId) => {
    try {
      await blogAPI.deleteBlog(blogId);
      message.success('博客删除成功');
      // 重新获取博客列表
      if (selectedCategory) {
        fetchBlogsByCategory(selectedCategory);
      } else {
        fetchAllBlogs();
      }
    } catch (error) {
      message.error('博客删除失败');
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 渲染博客列表
  const renderBlogList = () => {
    if (loading) {
      return <Spin size="large" />;
    }

    if (blogs.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <Title level={4}>暂无博客内容</Title>
          <Text type="secondary">
            {selectedCategory
              ? `该分类下暂无博客文章`
              : '还未创建任何博客文章'
            }
          </Text>
        </div>
      );
    }

    return (
      <List
        dataSource={blogs}
        renderItem={(blog) => (
          <List.Item>
            <Card
              hoverable
              style={{ width: '100%', marginBottom: 16 }}
              onClick={() => handleBlogClick(blog)}
            >
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Title level={4} style={{ marginBottom: 8 }}>
                    {blog.title}
                  </Title>
                  <Paragraph ellipsis={{ rows: 2 }} style={{ color: '#666' }}>
                    {blog.summary || blog.content?.substring(0, 150) + '...'}
                  </Paragraph>
                  <Space size="middle">
                    <Text type="secondary">
                      <CalendarOutlined /> {formatDate(blog.createTime)}
                    </Text>
                    <Text type="secondary">
                      <EyeOutlined /> {blog.viewCount || 0}
                    </Text>
                    <Text type="secondary">
                      <LikeOutlined /> {blog.likeCount || 0}
                    </Text>
                    {blog.category && (
                      <Tag color="blue" icon={<FolderOutlined />}>
                        {blog.category}
                      </Tag>
                    )}
                  </Space>
                </Col>
                <Col>
                  {isOwner && (
                    <Space size="small">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/${username}/blog/article/edit/?id=${blog.id}`);
                        }}
                      />
                      <Popconfirm
                        title="确认删除"
                        description="确定要删除这篇博客吗？"
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          handleDeleteBlog(blog.id);
                        }}
                        onCancel={(e) => e?.stopPropagation()}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    </Space>
                  )}
                  <Button
                    type="text"
                    icon={<RightOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBlogClick(blog);
                    }}
                  />
                </Col>
              </Row>
            </Card>
          </List.Item>
        )}
      />
    );
  };

  return (
    <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', padding: 24 }}>
      <Layout style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', width: '100%' }}>
        {/* 分类侧边栏 */}
        <Sider width={250} style={{ background: '#fafafa', padding: '24px 0' }}>
        <div style={{ padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>分类</Title>
          {isOwner && (
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/${username}/blog/new/`)}
            >
              创建
            </Button>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedCategory || 'all']}
          style={{ border: 'none', background: 'transparent' }}
        >
          <Menu.Item
            key="all"
            onClick={() => handleCategoryClick('all')}
            icon={<FolderOutlined />}
          >
            全部博客
          </Menu.Item>
          {categories.map(category => (
            <Menu.Item
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
              icon={<FolderOutlined />}
            >
              {category.name}
              <span style={{ color: '#999', marginLeft: 8 }}>
                ({category.blogCount || 0})
              </span>
            </Menu.Item>
          ))}
        </Menu>
      </Sider>

      {/* 主内容区域 */}
      <Content style={{ padding: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            {selectedCategory
              ? categories.find(cat => cat.id == selectedCategory)?.name + ' 的博客'
              : '所有博客'
            }
          </Title>
          {isOwner && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/${username}/blog/article/new/`)}
            >
              创建博客
            </Button>
          )}
        </div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          共 {blogs.length} 篇文章
        </Text>
        {renderBlogList()}
      </Content>
      </Layout>
    </div>
  );
};

export default BlogPage;